import type { Dispatch } from 'redux'
import { Cart, ResponseError, nativeAppHeaders } from '@ynap/api-utils'
import { timeout, delay } from '@ynap/promise-utils'
import { getWatsonConfig, getTimeout, getDelay } from '@ynap/cart-state/src/selectors/getConfig'
import { getLocale } from '@ynap/cart-state'
import { StoreState } from 'app/state/types'
import { CHECK_PROMOTION_SUCCESS, CHECK_PROMOTION_REQUEST, CHECK_PROMOTION_ERROR } from '../constants/ActionTypes'
import { getAdditionalCheckoutHeaders } from '../utils/checkoutActionUtils'

type CheckPromotionResponseData = {
    id: number
    description: string
    valid: 'true' | 'false'
    testResult: {
        [key: string]: {
            valid: 'true' | 'false'
            message?: string
            details?: string
        }
    }
}

type Payload = { code: string; data: Record<string, string | undefined> }

function checkPromotionRequest({ code }: { code: string }) {
    return {
        type: CHECK_PROMOTION_REQUEST,
        payload: {
            code,
        },
    }
}

function checkPromotionSuccess({ code, data }: Payload) {
    return {
        type: CHECK_PROMOTION_SUCCESS,
        payload: {
            code,
            data,
        },
    }
}

function checkPromotionError({ code, data }: Payload) {
    return {
        type: CHECK_PROMOTION_ERROR,
        payload: {
            code,
            data,
        },
    }
}

export default function checkPromotion({ code, id }: { code: string; id: never } | { code: never; id: string }) {
    return (dispatch: Dispatch, getState: () => StoreState, fetch: WindowOrWorkerGlobalScope['fetch']) => {
        const state = getState()
        const watsonConfig = getWatsonConfig(state)
        const { langId } = getLocale(state)
        const additionalHeaders = {
            ...nativeAppHeaders(state.deviceType),
            ...getAdditionalCheckoutHeaders(state),
        }

        const codeOrId = code || id
        dispatch(checkPromotionRequest({ code: codeOrId }))

        const request = Cart.checkPromotion({ code, id, ...watsonConfig, langId, additionalHeaders })

        return delay(timeout(fetch(request), getTimeout(state)), getDelay(state))
            .then((response: Response) => {
                if (!response.ok) {
                    throw new ResponseError(response)
                }
                return response.json()
            })
            .then((responseData: CheckPromotionResponseData) => {
                const { testResult, valid } = responseData

                const reasons: Record<string, string | undefined> = {}
                testResult &&
                    Object.entries(testResult)
                        .filter(([_key, value]) => value.valid === 'false')
                        .forEach(([key, value]) => {
                            reasons[key] = value?.message || value?.details
                        })

                const data = {
                    valid: valid === 'true' || !testResult,
                    reasons,
                    loading: false,
                }

                dispatch(checkPromotionSuccess({ code: codeOrId, data }))
            })
            .catch((error: any) => {
                const { message } = error
                dispatch(checkPromotionError({ code: codeOrId, data: { error: message, loading: false } }))
            })
    }
}
